/**
 * AI SupportAgent v2 policy — grouped, fully labelled admin page:
 * model & credentials, reply policy, language & tone, verification/tagging,
 * capabilities (vision, voice, location), and the knowledge base.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Bot, Eye, Globe, KeyRound, Languages, MapPin, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import {
  useAiSettings,
  usePatchAiSettings,
  type CrmAiSettings,
} from '@/api/bi/crmSettings';
import { notify } from '@/lib/toast';

export function CrmAiSettingsPage() {
  const q = useAiSettings();
  const patch = usePatchAiSettings();
  const [form, setForm] = useState<CrmAiSettings | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyDirty, setApiKeyDirty] = useState(false);

  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  if (q.isLoading || !form) return <Skeleton className="h-96 w-full" />;
  if (q.isError) return <ErrorState title="Failed to load AI settings" onRetry={() => q.refetch()} />;

  function set<K extends keyof CrmAiSettings>(key: K, value: CrmAiSettings[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function handleSave() {
    const f = form!;
    const payload: Record<string, unknown> = {
      is_enabled: f.is_enabled,
      model: f.model,
      base_url: f.base_url,
      confidence_threshold: f.confidence_threshold,
      auto_intents: f.auto_intents,
      escalate_intents: f.escalate_intents,
      max_auto_replies_per_hour: f.max_auto_replies_per_hour,
      system_prompt: f.system_prompt,
      faq_corpus: f.faq_corpus,
      require_order_and_mobile: f.require_order_and_mobile,
      auto_tag_on_verify: f.auto_tag_on_verify,
      reply_language: f.reply_language,
      chat_style_notes: f.chat_style_notes,
      email_style_notes: f.email_style_notes,
      email_greeting_style: f.email_greeting_style,
      email_signature: f.email_signature,
      vision_enabled: f.vision_enabled,
      transcription_enabled: f.transcription_enabled,
      transcription_model: f.transcription_model,
      smart_compose_enabled: f.smart_compose_enabled,
      location_link_ttl_minutes: f.location_link_ttl_minutes,
    };
    if (apiKeyDirty) payload.api_key = apiKey;
    try {
      await patch.mutateAsync(payload);
      notify.success('AI settings saved');
      setApiKey('');
      setApiKeyDirty(false);
    } catch {
      notify.error('Could not save AI settings');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="AI agent"
        description="The SupportAgent answers customers across WhatsApp, Instagram, Messenger, and email — auto-replying to high-confidence intents, drafting the rest for review, and escalating sensitive issues."
        actions={
          <Button onClick={() => void handleSave()} disabled={patch.isPending}>
            {patch.isPending ? 'Saving…' : 'Save all'}
          </Button>
        }
      />
      <div className="space-y-4">
        <Section
          icon={<KeyRound className="h-4 w-4" />}
          title="Model & credentials"
          hint="Any OpenAI-compatible provider works. Vision and voice features require a multimodal model (e.g. gpt-4o)."
        >
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input type="checkbox" checked={form.is_enabled} onChange={(e) => set('is_enabled', e.target.checked)} />
            <span>
              <span className="font-medium">Enable AI agent</span>
              <span className="block text-xs text-muted-foreground">
                When off, every inbound message waits for a human — nothing is auto-sent.
              </span>
            </span>
          </label>
          <Field label="Model" hint="Chat model used for analysis and replies.">
            <Input value={form.model} onChange={(e) => set('model', e.target.value)} />
          </Field>
          <Field label="Base URL (optional)" hint="Leave empty for api.openai.com.">
            <Input value={form.base_url} placeholder="https://api.openai.com/v1" onChange={(e) => set('base_url', e.target.value)} />
          </Field>
          <Field label={`API key ${form.has_api_key ? '(configured)' : '(not set)'}`} hint="Stored encrypted. Leave blank to keep the current key.">
            <Input
              type="password"
              value={apiKey}
              placeholder={form.has_api_key ? '••••••••' : 'sk-…'}
              onChange={(e) => {
                setApiKey(e.target.value);
                setApiKeyDirty(true);
              }}
            />
          </Field>
        </Section>

        <Section
          icon={<Bot className="h-4 w-4" />}
          title="Reply policy"
          hint="What the agent may send on its own versus what needs human review."
        >
          <Field
            label="Confidence threshold"
            hint="Replies below this confidence become drafts for human review instead of auto-sending."
          >
            <Input
              type="number" step="0.01" min={0} max={1}
              value={form.confidence_threshold}
              onChange={(e) => set('confidence_threshold', Number(e.target.value))}
            />
          </Field>
          <Field label="Max auto-replies per hour (per conversation)" hint="Loop protection — beyond this the agent drafts instead.">
            <Input
              type="number"
              value={form.max_auto_replies_per_hour}
              onChange={(e) => set('max_auto_replies_per_hour', Number(e.target.value))}
            />
          </Field>
          <Field
            label="Auto-send intents"
            hint="Comma-separated. High-confidence replies for these intents go out without review."
            wide
          >
            <Input
              value={(form.auto_intents || []).join(', ')}
              onChange={(e) => set('auto_intents', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            />
          </Field>
          <Field
            label="Always-escalate intents"
            hint="Comma-separated. These always create a ticket for a human (refunds, payments, legal…)."
            wide
          >
            <Input
              value={(form.escalate_intents || []).join(', ')}
              onChange={(e) => set('escalate_intents', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={form.smart_compose_enabled}
              onChange={(e) => set('smart_compose_enabled', e.target.checked)}
            />
            <span>
              <span className="font-medium">Smart compose for agents</span>
              <span className="block text-xs text-muted-foreground">
                The ✨ button in the inbox composer drafts a reply for human agents on demand.
              </span>
            </span>
          </label>
        </Section>

        <Section
          icon={<ShieldCheck className="h-4 w-4" />}
          title="Identity verification & tagging"
          hint="How the agent protects order information and links conversations to customers."
        >
          <label className="flex items-start gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.require_order_and_mobile}
              onChange={(e) => set('require_order_and_mobile', e.target.checked)}
            />
            <span>
              <span className="font-medium">Require order number + registered mobile before sharing order details</span>
              <span className="block text-xs text-muted-foreground">
                The verification tool only returns order data when both match — the agent never reveals whether an
                order exists otherwise. Strongly recommended.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.auto_tag_on_verify}
              onChange={(e) => set('auto_tag_on_verify', e.target.checked)}
            />
            <span>
              <span className="font-medium">Auto-tag customer & order once verified</span>
              <span className="block text-xs text-muted-foreground">
                The moment a mobile number or order is verified, the conversation is linked to the customer
                (visible in the inbox context panel with Customer 360 click-through).
              </span>
            </span>
          </label>
        </Section>

        <Section
          icon={<Languages className="h-4 w-4" />}
          title="Language & tone"
          hint="Replies are always simple, professional English — even when customers write in Nepali or romanized Nepali. Email gets a formal structure; chat stays short."
        >
          <Field label="Reply language" hint="'english' enforces English-only replies on every channel.">
            <Input value={form.reply_language} onChange={(e) => set('reply_language', e.target.value)} />
          </Field>
          <Field label="Email greeting" hint="{name} is replaced with the customer's first name.">
            <Input value={form.email_greeting_style} onChange={(e) => set('email_greeting_style', e.target.value)} />
          </Field>
          <Field label="Chat style (WhatsApp / Instagram / Messenger)" hint="Tone guidance appended to the prompt for chat channels." wide>
            <textarea
              className="min-h-[64px] w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={form.chat_style_notes}
              onChange={(e) => set('chat_style_notes', e.target.value)}
            />
          </Field>
          <Field label="Email style" hint="Tone guidance for Gmail replies — proper business emails, never chat-style." wide>
            <textarea
              className="min-h-[64px] w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={form.email_style_notes}
              onChange={(e) => set('email_style_notes', e.target.value)}
            />
          </Field>
          <Field label="Email signature" hint="Appended to every AI email reply." wide>
            <textarea
              className="min-h-[64px] w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={form.email_signature}
              onChange={(e) => set('email_signature', e.target.value)}
            />
          </Field>
        </Section>

        <Section
          icon={<Eye className="h-4 w-4" />}
          title="Vision & voice"
          hint="Understand screenshots, product photos, and voice notes."
        >
          <label className="flex items-start gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.vision_enabled}
              onChange={(e) => set('vision_enabled', e.target.checked)}
            />
            <span>
              <span className="font-medium">Analyze customer images</span>
              <span className="block text-xs text-muted-foreground">
                Order screenshots, damaged-product photos, and payment receipts are passed to the model. Requires a
                vision-capable model.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.transcription_enabled}
              onChange={(e) => set('transcription_enabled', e.target.checked)}
            />
            <span>
              <span className="font-medium">Transcribe voice notes</span>
              <span className="block text-xs text-muted-foreground">
                WhatsApp/Instagram audio is transcribed and answered like text.
              </span>
            </span>
          </label>
          <Field label="Transcription model" hint="e.g. whisper-1">
            <Input value={form.transcription_model} onChange={(e) => set('transcription_model', e.target.value)} />
          </Field>
        </Section>

        <Section
          icon={<MapPin className="h-4 w-4" />}
          title="Location capture"
          hint="For product availability, coverage, and delivery ETA answers. WhatsApp uses the native share-location button; other channels get a secure one-time link."
        >
          <Field label="Location link validity (minutes)" hint="Magic links sent on Instagram/Messenger/email expire after this.">
            <Input
              type="number" min={5}
              value={form.location_link_ttl_minutes}
              onChange={(e) => set('location_link_ttl_minutes', Number(e.target.value))}
            />
          </Field>
        </Section>

        <Section
          icon={<Globe className="h-4 w-4" />}
          title="Knowledge base"
          hint="The operating prompt defines the agent's rules; the FAQ corpus is its reference material."
        >
          <Field label="System prompt" hint="Identity, verification, escalation, and language rules. Edit carefully." wide>
            <textarea
              className="min-h-[220px] w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs"
              value={form.system_prompt}
              onChange={(e) => set('system_prompt', e.target.value)}
            />
          </Field>
          <Field label="FAQ corpus" hint="Plain text, one topic per paragraph (blank line between topics). Searched by the agent." wide>
            <textarea
              className="min-h-[160px] w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={form.faq_corpus}
              onChange={(e) => set('faq_corpus', e.target.value)}
            />
          </Field>
        </Section>

        {form.last_error ? (
          <p className="text-xs text-danger">
            Last agent error: <span className="font-mono">{form.last_error}</span>
          </p>
        ) : null}
        <div className="flex justify-end pb-6">
          <Button onClick={() => void handleSave()} disabled={patch.isPending}>
            {patch.isPending ? 'Saving…' : 'Save all'}
          </Button>
        </div>
      </div>
    </>
  );
}

function Section({
  icon,
  title,
  hint,
  children,
}: {
  icon: ReactNode;
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2 text-sm">
          {icon} {title}
        </CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardHeader>
      <CardBody className="grid gap-4 pt-0 md:grid-cols-2">{children}</CardBody>
    </Card>
  );
}
